/**
 * 发品阶段建议零售价（RRP）：规则归一化、SKU 表单结构、publishOrEdit 出站映射与校验。
 * 文档：shein-product-prices.md §4.1
 */

import { fixedUi } from '@/i18n/fixedUi';
import { rrpMaterialRequirementLabel } from '@/shein-platform/modules/discuss/rrpDisplay';

export function normalizeRrpRule(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw.data && typeof raw.data === 'object' ? raw.data : raw;
  const availableSites = Array.isArray(data.available_sites)
    ? data.available_sites
    : (Array.isArray(data.availableSites) ? data.availableSites : []);
  return {
    isRrpRequired: Number(data.is_rrp_required ?? data.isRrpRequired ?? 0) === 1,
    siteFillingRule: Number(data.site_filling_rule ?? data.siteFillingRule ?? 0) === 1,
    nonSiteFillingRule: Number(data.non_site_filling_rule ?? data.nonSiteFillingRule ?? 0) === 1,
    availableSites: availableSites.map((s) => ({
      site: String(s?.site || s?.site_abbr || s?.siteAbbr || '').trim(),
      currency: String(s?.currency || '').trim(),
      materialRequirement: Number(s?.material_requirement ?? s?.materialRequirement ?? 0),
    })).filter((s) => s.site),
    nonSiteCurrencies: (Array.isArray(data.non_site_currencies)
      ? data.non_site_currencies
      : (Array.isArray(data.nonSiteCurrencies) ? data.nonSiteCurrencies : []))
      .map((c) => String(c || '').trim())
      .filter(Boolean),
    raw: data,
  };
}

/** 规则是否要求/允许填写 RRP（两端都为 0 则无需填写）。rule 为空表示尚未判定。 */
export function rrpRuleNeedsFill(rule) {
  if (!rule) return false;
  return Boolean(rule.siteFillingRule || rule.nonSiteFillingRule);
}

/** 已成功拿到规则，且两端均为不允许填写 */
export function rrpRuleExplicitlyNoFill(rule) {
  return Boolean(rule) && !rrpRuleNeedsFill(rule);
}

export function emptySiteRrpEntry({
  siteAbbr = '',
  currency = '',
  materialRequirement = 0,
} = {}) {
  return {
    siteAbbr: String(siteAbbr || '').trim(),
    currency: String(currency || '').trim(),
    price: '',
    materialRequirement: Number(materialRequirement) || 0,
    typeOneAttachments: [],
    typeOneLink: '',
    typeTwoAttachments: [],
    typeTwoLink: '',
  };
}

/**
 * 按规则 + 已选上架站点，生成/对齐 SKU 的 siteRrpInfoList（保留已填价格与材料）。
 */
export function syncSkuSiteRrpInfoList(existingList, rule, selectedSites = []) {
  if (!rrpRuleNeedsFill(rule)) return [];
  const selected = new Set(
    (Array.isArray(selectedSites) ? selectedSites : [])
      .map((s) => String(s || '').trim())
      .filter(Boolean),
  );
  const prev = Array.isArray(existingList) ? existingList : [];
  const byKey = new Map();
  prev.forEach((row) => {
    const key = `${String(row.siteAbbr || '').trim()}|${String(row.currency || '').trim()}`;
    byKey.set(key, row);
  });

  const next = [];
  if (rule.siteFillingRule) {
    const sites = rule.availableSites || [];
    sites.forEach((s) => {
      // 若用户已选上架站点，则仅要求「选中 ∩ 分站点规则」；未选站点时展示全部规则站点
      if (selected.size && !selected.has(s.site)) return;
      const key = `${s.site}|${s.currency}`;
      const old = byKey.get(key);
      next.push(old
        ? {
          ...emptySiteRrpEntry(s),
          ...old,
          siteAbbr: s.site,
          currency: s.currency || old.currency,
          materialRequirement: s.materialRequirement,
        }
        : emptySiteRrpEntry({
          siteAbbr: s.site,
          currency: s.currency,
          materialRequirement: s.materialRequirement,
        }));
    });
  }

  if (rule.nonSiteFillingRule) {
    const currencies = rule.nonSiteCurrencies?.length
      ? rule.nonSiteCurrencies
      : ['USD'];
    const oldNon = prev.find((r) => !String(r.siteAbbr || '').trim()) || null;
    const currency = oldNon?.currency && currencies.includes(oldNon.currency)
      ? oldNon.currency
      : currencies[0];
    next.push({
      ...emptySiteRrpEntry({ siteAbbr: '', currency, materialRequirement: 0 }),
      ...(oldNon || {}),
      siteAbbr: '',
      currency,
      materialRequirement: 0,
    });
  }
  return next;
}

export function applyRrpListToFormSkcList(skcList, rule, selectedSites) {
  return (Array.isArray(skcList) ? skcList : []).map((skc) => ({
    ...skc,
    skuRows: (skc.skuRows || []).map((sku) => ({
      ...sku,
      siteRrpInfoList: syncSkuSiteRrpInfoList(sku.siteRrpInfoList, rule, selectedSites),
    })),
  }));
}

function buildProof(attachments, link) {
  const files = (Array.isArray(attachments) ? attachments : [])
    .map((u) => String(u || '').trim())
    .filter(Boolean);
  const proofLink = String(link || '').trim();
  if (!files.length && !proofLink) return undefined;
  const out = {};
  if (files.length) out.attachments = files.slice(0, 6);
  if (proofLink) out.proof_link = proofLink;
  return out;
}

/** 发品 publishOrEdit：SKU.site_rrp_info_list */
export function buildPublishSiteRrpInfoList(sku) {
  const list = Array.isArray(sku?.siteRrpInfoList) ? sku.siteRrpInfoList : [];
  const out = [];
  for (const row of list) {
    const price = String(row.price ?? '').trim();
    if (!price) continue;
    const item = {
      currency: String(row.currency || '').trim() || undefined,
      price: Number(price),
    };
    const site = String(row.siteAbbr || '').trim();
    if (site) item.site_abbr = site;
    const typeOne = buildProof(row.typeOneAttachments, row.typeOneLink);
    const typeTwo = buildProof(row.typeTwoAttachments, row.typeTwoLink);
    if (typeOne) item.type_one_proof = typeOne;
    if (typeTwo) item.type_two_proof = typeTwo;
    out.push(item);
  }
  return out;
}

function proofSatisfied(row, requirement) {
  const n = Number(requirement);
  if (!n) return true;
  const hasOne = (row.typeOneAttachments?.length > 0) || Boolean(String(row.typeOneLink || '').trim());
  const hasTwo = (row.typeTwoAttachments?.length > 0) || Boolean(String(row.typeTwoLink || '').trim());
  if (n === 1) return hasOne;
  if (n === 2) return hasOne || hasTwo;
  return true;
}

/**
 * 发品前 RRP 校验。返回 { ok, message, field, section }
 */
export function validatePublishRrp(form, rule) {
  // 未拉到规则：不能当成「不用填」
  if (!rule) {
    return {
      ok: false,
      field: 'rrp',
      section: 'spec',
      message: '尚未获取建议零售价填写规则，请稍后重试或刷新页面',
    };
  }
  if (!rrpRuleNeedsFill(rule)) {
    return { ok: true };
  }
  const required = Boolean(rule.isRrpRequired);
  const skcs = Array.isArray(form?.skcList) ? form.skcList : [];
  let anyPrice = false;

  for (const skc of skcs) {
    for (const sku of skc.skuRows || []) {
      const list = Array.isArray(sku.siteRrpInfoList) ? sku.siteRrpInfoList : [];
      for (const row of list) {
        const price = String(row.price ?? '').trim();
        if (!price) {
          if (required) {
            const siteLabel = row.siteAbbr || '其他站点';
            return {
              ok: false,
              field: 'rrp',
              section: 'spec',
              message: `建议零售价必填：请填写 ${skc.colorLabel || fixedUi("颜色")} / ${sku.sizeLabel || '规格'} 的 ${siteLabel} 价格`,
            };
          }
          continue;
        }
        anyPrice = true;
        if (!String(row.currency || '').trim()) {
          return {
            ok: false,
            field: 'rrp',
            section: 'spec',
            message: '建议零售价缺少币种',
          };
        }
        if (!proofSatisfied(row, row.materialRequirement)) {
          return {
            ok: false,
            field: 'rrp',
            section: 'spec',
            message: `请按要求上传证明材料（${rrpMaterialRequirementLabel(row.materialRequirement)}）：${row.siteAbbr || '其他站点'}`,
          };
        }
      }
    }
  }

  if (required && !anyPrice) {
    return {
      ok: false,
      field: 'rrp',
      section: 'spec',
      message: '当前品牌要求填写建议零售价',
    };
  }
  return { ok: true };
}

export function siteDisplayLabel(siteAbbr) {
  const s = String(siteAbbr || '').trim().toLowerCase();
  if (!s) return '其他站点';
  const map = {
    'shein-de': '德国站',
    'shein-fr': '法国站',
    'shein-it': '意大利站',
    'shein-es': '西班牙站',
    'shein-uk': '英国站',
    'shein-us': '美国站',
    'shein-nl': '荷兰站',
    'shein-pl': '波兰站',
    'shein-se': '瑞典站',
    'shein-pt': '葡萄牙站',
    'shein-eu': '欧洲站',
  };
  return map[s] || siteAbbr;
}
